import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { generateFromAnnotations } from '../../lib/template_generator';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Upload,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Pencil,
  FileText,
  Loader2,
  Save,
  MousePointer2,
  FileCheck,
} from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────────────

const EMPTY_ANN_FORM = {
  fieldKey: '',
  label: '',
  sectionTitle: '',
  fieldType: 'text',
  fontSize: 10,
  visibility: 'public',
  editable_by: 'player',
  required: false,
  min: '',
  max: '',
  resourceMaxKey: '',
};

const FIELD_TYPES = [
  { value: 'text', labelFr: 'Texte', labelEn: 'Text' },
  { value: 'number', labelFr: 'Nombre', labelEn: 'Number' },
  { value: 'multiline', labelFr: 'Multiligne', labelEn: 'Multiline' },
  { value: 'checkbox', labelFr: 'Case à cocher', labelEn: 'Checkbox' },
  { value: 'select', labelFr: 'Liste déroulante', labelEn: 'Dropdown' },
  { value: 'resource_bar', labelFr: 'Barre de ressource', labelEn: 'Resource bar' },
  { value: 'dice_value', labelFr: 'Valeur de dé', labelEn: 'Dice value' },
  { value: 'repeatable_list', labelFr: 'Liste répétable', labelEn: 'Repeatable list' },
];

const ANN_COLORS = [
  'rgba(99,102,241,0.55)',
  'rgba(239,68,68,0.55)',
  'rgba(16,185,129,0.55)',
  'rgba(245,158,11,0.55)',
  'rgba(59,130,246,0.55)',
  'rgba(236,72,153,0.55)',
  'rgba(14,165,233,0.55)',
  'rgba(168,85,247,0.55)',
];

function annColor(idx) {
  return ANN_COLORS[idx % ANN_COLORS.length];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function pct(v) {
  return `${(v * 100).toFixed(3)}%`;
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function AdminPdfBuilderPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { language } = useAuth();

  // ── template ──
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── pdf.js ──
  const [pdfjsReady, setPdfjsReady] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [renderingPage, setRenderingPage] = useState(false);

  // ── upload ──
  const [pdfAssetId, setPdfAssetId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // ── drawing ──
  const [drawing, setDrawing] = useState(false);
  const drawStartRef = useRef(null);
  const [previewRect, setPreviewRect] = useState(null); // { x, y, w, h } normalized

  // ── annotations ──
  const [annotations, setAnnotations] = useState([]);
  const [annModalOpen, setAnnModalOpen] = useState(false);
  const [annForm, setAnnForm] = useState(EMPTY_ANN_FORM);
  const [pendingRect, setPendingRect] = useState(null); // rect for new ann
  const [editingAnnId, setEditingAnnId] = useState(null);

  // ── save ──
  const [saving, setSaving] = useState(false);

  // ── refs ──
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // ── Load pdf.js from CDN ─────────────────────────────────────────────────

  useEffect(() => {
    if (window.pdfjsLib) {
      setPdfjsReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src =
      'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      setPdfjsReady(true);
    };
    script.onerror = () => {
      toast.error(
        language === 'fr'
          ? 'Impossible de charger le moteur PDF'
          : 'Failed to load PDF engine'
      );
    };
    document.head.appendChild(script);
  }, [language]);

  // ── Fetch template ────────────────────────────────────────────────────────

  const fetchTemplate = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/system-templates/${templateId}`);
      setTemplate(res.data);
      if (res.data.pdf_asset_id) {
        setPdfAssetId(res.data.pdf_asset_id);
      }
      if (res.data.builder_annotations) {
        setAnnotations(res.data.builder_annotations);
      }
    } catch {
      toast.error(t('error_generic', language));
    } finally {
      setLoading(false);
    }
  }, [templateId, language]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  // ── Load PDF when asset is available ─────────────────────────────────────

  useEffect(() => {
    if (!pdfjsReady || !pdfAssetId) return;

    let cancelled = false;
    async function loadPdf() {
      try {
        // Fetch PDF bytes from backend
        const res = await axios.get(
          `/api/admin/system-assets/${pdfAssetId}/file`,
          { responseType: 'arraybuffer' }
        );
        if (cancelled) return;
        const typedArray = new Uint8Array(res.data);
        const doc = await window.pdfjsLib.getDocument({ data: typedArray })
          .promise;
        if (cancelled) return;
        setPdfDoc(doc);
        setTotalPages(doc.numPages);
        setCurrentPage(1);
      } catch {
        if (!cancelled)
          toast.error(
            language === 'fr'
              ? 'Impossible de charger le PDF'
              : 'Failed to load PDF'
          );
      }
    }
    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [pdfjsReady, pdfAssetId, language]);

  // ── Render page ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current || !containerRef.current) return;

    let cancelled = false;
    async function renderPage() {
      setRenderingPage(true);
      try {
        const page = await pdfDoc.getPage(currentPage);
        if (cancelled) return;

        const container = containerRef.current;
        const containerWidth = container.clientWidth || 600;
        const viewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport: scaledViewport })
          .promise;

        if (!cancelled) {
          setCanvasSize({
            width: scaledViewport.width,
            height: scaledViewport.height,
          });
        }
      } catch {
        /* ignore render errors */
      } finally {
        if (!cancelled) setRenderingPage(false);
      }
    }

    renderPage();
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, currentPage]);

  // ── Mouse drawing handlers ────────────────────────────────────────────────

  function getRelPos(e) {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    };
  }

  function handleMouseDown(e) {
    if (e.button !== 0) return;
    e.preventDefault();
    drawStartRef.current = getRelPos(e);
    setDrawing(true);
    setPreviewRect(null);
  }

  function handleMouseMove(e) {
    if (!drawing || !drawStartRef.current) return;
    e.preventDefault();
    const cur = getRelPos(e);
    const s = drawStartRef.current;
    setPreviewRect({
      x: Math.min(s.x, cur.x),
      y: Math.min(s.y, cur.y),
      w: Math.abs(cur.x - s.x),
      h: Math.abs(cur.y - s.y),
    });
  }

  function handleMouseUp(e) {
    if (!drawing || !drawStartRef.current) return;
    e.preventDefault();
    setDrawing(false);
    const end = getRelPos(e);
    const s = drawStartRef.current;
    drawStartRef.current = null;

    const x = Math.min(s.x, end.x);
    const y = Math.min(s.y, end.y);
    const w = Math.abs(end.x - s.x);
    const h = Math.abs(end.y - s.y);

    setPreviewRect(null);

    if (w < 0.01 || h < 0.005) return; // rect trop petit, ignore

    setPendingRect({ x, y, width: w, height: h });
    setEditingAnnId(null);
    setAnnForm(EMPTY_ANN_FORM);
    setAnnModalOpen(true);
  }

  // ── Annotation CRUD ───────────────────────────────────────────────────────

  function openEditAnn(ann) {
    setEditingAnnId(ann.id);
    setAnnForm({
      fieldKey: ann.fieldKey || '',
      label: ann.label || '',
      sectionTitle: ann.sectionTitle || '',
      fieldType: ann.fieldType || 'text',
      fontSize: ann.fontSize ?? 10,
      visibility: ann.permissions?.visibility || 'public',
      editable_by: ann.permissions?.editable_by || 'player',
      required: ann.validations?.required || false,
      min: ann.validations?.min ?? '',
      max: ann.validations?.max ?? '',
      resourceMaxKey: ann.resourceMaxKey || '',
    });
    setPendingRect(null);
    setAnnModalOpen(true);
  }

  function handleSaveAnn() {
    if (!annForm.fieldKey.trim()) {
      toast.error(
        language === 'fr'
          ? 'La clé du champ est obligatoire'
          : 'Field key is required'
      );
      return;
    }

    if (editingAnnId) {
      // update existing
      setAnnotations((prev) =>
        prev.map((a) =>
          a.id === editingAnnId
            ? {
                ...a,
                fieldKey: annForm.fieldKey.trim(),
                label: annForm.label,
                sectionTitle: annForm.sectionTitle,
                fieldType: annForm.fieldType,
                fontSize: Number(annForm.fontSize) || 10,
                permissions: {
                  visibility: annForm.visibility,
                  editable_by: annForm.editable_by,
                },
                validations: {
                  required: annForm.required,
                  min: annForm.min,
                  max: annForm.max,
                },
                resourceMaxKey: annForm.resourceMaxKey,
              }
            : a
        )
      );
    } else if (pendingRect) {
      // create new
      const newAnn = {
        id: `ann_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        page: currentPage - 1, // 0-based
        rect: pendingRect,
        fieldKey: annForm.fieldKey.trim(),
        label: annForm.label,
        sectionTitle: annForm.sectionTitle,
        fieldType: annForm.fieldType,
        fontSize: Number(annForm.fontSize) || 10,
        permissions: {
          visibility: annForm.visibility,
          editable_by: annForm.editable_by,
        },
        validations: {
          required: annForm.required,
          min: annForm.min,
          max: annForm.max,
        },
        resourceMaxKey: annForm.resourceMaxKey,
      };
      setAnnotations((prev) => [...prev, newAnn]);
    }

    setAnnModalOpen(false);
    setPendingRect(null);
    setEditingAnnId(null);
  }

  function deleteAnn(id) {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
  }

  // ── Save to backend ───────────────────────────────────────────────────────

  async function handleSave() {
    if (!pdfAssetId) {
      toast.error(
        language === 'fr'
          ? 'Veuillez d\'abord téléverser un PDF'
          : 'Please upload a PDF first'
      );
      return;
    }

    setSaving(true);
    try {
      const generated = generateFromAnnotations(annotations);
      await axios.post(`/api/system-templates/${templateId}/pdf-builder`, {
        pdf_asset_id: pdfAssetId,
        annotations,
        generated,
      });
      toast.success(
        language === 'fr'
          ? 'Template mis à jour avec succès'
          : 'Template saved successfully'
      );
      fetchTemplate();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setSaving(false);
    }
  }

  // ── Upload PDF ────────────────────────────────────────────────────────────

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(language === 'fr' ? 'Fichier PDF requis' : 'PDF file required');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('system_id', template?.system_id || '');

      // Laisser axios/navigateur gérer automatiquement Content-Type + boundary
      const res = await axios.post('/api/admin/system-assets', formData);
      setPdfAssetId(res.data.id);
      toast.success(
        language === 'fr'
          ? `PDF chargé (${res.data.page_count} page(s))`
          : `PDF loaded (${res.data.page_count} page(s))`
      );
    } catch (err) {
      console.error('[PDF upload error]', err.response?.data || err.message);
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join(', ')
        : detail || t('error_generic', language);
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const pageAnnotations = annotations.filter(
    (a) => a.page === currentPage - 1
  );

  const isFr = language === 'fr';

  return (
    <AdminLayout>
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <button
            onClick={() => navigate(`/admin/game-systems/${template?.system_id}/templates`)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-1"
          >
            <ArrowLeft className="h-4 w-4" />
            {isFr ? 'Templates' : 'Templates'}
          </button>
          <h1 className="text-xl font-cinzel font-bold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {isFr ? 'PDF Builder' : 'PDF Builder'}
            {template && (
              <span className="text-sm font-normal text-muted-foreground ml-1">
                — {template.name}
              </span>
            )}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          {/* Upload button */}
          <Button
            variant="outline"
            size="sm"
            disabled={uploading || !template}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            {isFr ? 'Charger un PDF' : 'Load PDF'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={handleFileChange}
          />

          {/* Save button */}
          <Button
            className="fantasy-button"
            size="sm"
            disabled={saving || !pdfAssetId}
            onClick={handleSave}
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isFr ? 'Générer & Sauvegarder' : 'Generate & Save'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="flex gap-4 h-[calc(100vh-180px)]">

          {/* ── Left: PDF viewer ── */}
          <div className="flex flex-col flex-1 min-w-0">

            {/* Page navigation */}
            {pdfDoc && (
              <div className="flex items-center justify-center gap-3 mb-2">
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={currentPage <= 1 || renderingPage}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {isFr ? `Page ${currentPage} / ${totalPages}` : `Page ${currentPage} of ${totalPages}`}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={currentPage >= totalPages || renderingPage}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Canvas container */}
            <div
              ref={containerRef}
              className="relative flex-1 overflow-auto border border-border rounded-lg bg-muted/20"
            >
              {!pdfAssetId ? (
                /* Upload prompt */
                <div className="flex flex-col items-center justify-center h-full gap-4 text-muted-foreground">
                  <FileText className="h-16 w-16 opacity-30" />
                  <p className="text-sm">
                    {isFr
                      ? 'Téléversez un PDF de fiche de personnage pour commencer'
                      : 'Upload a character sheet PDF to get started'}
                  </p>
                  <Button
                    variant="outline"
                    disabled={uploading || !template}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {isFr ? 'Choisir un PDF' : 'Choose PDF'}
                  </Button>
                </div>
              ) : !pdfDoc ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                /* Drawing area */
                <div className="relative inline-block">
                  <canvas ref={canvasRef} className="block" />

                  {/* Overlay for drawing + annotations */}
                  <div
                    className="absolute inset-0"
                    style={{ cursor: drawing ? 'crosshair' : 'crosshair' }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={() => {
                      if (drawing) {
                        setDrawing(false);
                        setPreviewRect(null);
                        drawStartRef.current = null;
                      }
                    }}
                  >
                    {/* Preview rectangle while drawing */}
                    {previewRect && (
                      <div
                        className="absolute pointer-events-none border-2 border-primary bg-primary/20"
                        style={{
                          left: pct(previewRect.x),
                          top: pct(previewRect.y),
                          width: pct(previewRect.w),
                          height: pct(previewRect.h),
                        }}
                      />
                    )}

                    {/* Existing annotations for this page */}
                    {pageAnnotations.map((ann, idx) => (
                      <div
                        key={ann.id}
                        className="absolute group"
                        style={{
                          left: pct(ann.rect.x),
                          top: pct(ann.rect.y),
                          width: pct(ann.rect.width),
                          height: pct(ann.rect.height),
                          backgroundColor: annColor(
                            annotations.findIndex((a) => a.id === ann.id)
                          ),
                          border: '1.5px solid rgba(0,0,0,0.25)',
                        }}
                      >
                        {/* Tooltip-style label */}
                        <div
                          className="absolute top-0 left-0 text-[9px] leading-none bg-black/70 text-white px-0.5 rounded-br truncate max-w-full pointer-events-none"
                          style={{ fontSize: '9px' }}
                        >
                          {ann.fieldKey}
                        </div>

                        {/* Action buttons on hover */}
                        <div className="absolute top-0 right-0 hidden group-hover:flex gap-0.5 p-0.5">
                          <button
                            className="bg-white/90 rounded p-0.5 hover:bg-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditAnn(ann);
                            }}
                          >
                            <Pencil className="h-2.5 w-2.5 text-gray-700" />
                          </button>
                          <button
                            className="bg-white/90 rounded p-0.5 hover:bg-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteAnn(ann.id);
                            }}
                          >
                            <Trash2 className="h-2.5 w-2.5 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Drawing hint */}
            {pdfDoc && (
              <p className="text-xs text-muted-foreground mt-2 text-center flex items-center justify-center gap-1">
                <MousePointer2 className="h-3.5 w-3.5" />
                {isFr
                  ? 'Cliquez-glissez sur le PDF pour créer un champ'
                  : 'Click and drag on the PDF to create a field'}
              </p>
            )}
          </div>

          {/* ── Right: Annotations sidebar ── */}
          <div className="w-72 flex flex-col shrink-0 overflow-hidden border border-border rounded-lg">
            <div className="px-3 py-2 border-b border-border bg-muted/30">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <FileCheck className="h-4 w-4" />
                {isFr ? 'Champs annotés' : 'Annotated fields'}
                {annotations.length > 0 && (
                  <Badge variant="secondary" className="text-xs ml-auto">
                    {annotations.length}
                  </Badge>
                )}
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto">
              {annotations.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground text-center">
                  {isFr
                    ? 'Aucun champ annoté pour l\'instant'
                    : 'No annotated fields yet'}
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {annotations.map((ann, idx) => (
                    <li key={ann.id} className="px-3 py-2 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="inline-block h-2 w-2 rounded-full shrink-0"
                              style={{
                                backgroundColor: annColor(idx).replace(
                                  /[\d.]+\)$/,
                                  '1)'
                                ),
                              }}
                            />
                            <code className="text-xs font-mono truncate">
                              {ann.fieldKey}
                            </code>
                          </div>
                          {ann.label && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate pl-3.5">
                              {ann.label}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-1 mt-0.5 pl-3.5">
                            <Badge
                              variant="outline"
                              className="text-[10px] h-4 px-1"
                            >
                              p.{ann.page + 1}
                            </Badge>
                            <Badge
                              variant="outline"
                              className="text-[10px] h-4 px-1"
                            >
                              {ann.fieldType}
                            </Badge>
                            {ann.sectionTitle && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] h-4 px-1 truncate max-w-[120px]"
                              >
                                {ann.sectionTitle}
                              </Badge>
                            )}
                            {ann.permissions?.visibility === 'gm_only' && (
                              <Badge
                                variant="destructive"
                                className="text-[10px] h-4 px-1"
                              >
                                GM
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            className="p-1 rounded hover:bg-muted"
                            onClick={() => openEditAnn(ann)}
                          >
                            <Pencil className="h-3 w-3 text-muted-foreground" />
                          </button>
                          <button
                            className="p-1 rounded hover:bg-muted"
                            onClick={() => deleteAnn(ann.id)}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Summary */}
            {annotations.length > 0 && (
              <div className="px-3 py-2 border-t border-border bg-muted/20 text-xs text-muted-foreground">
                {[...new Set(annotations.map((a) => a.sectionTitle || isFr ? 'Général' : 'General'))].length}{' '}
                {isFr ? 'section(s)' : 'section(s)'}
                {' · '}
                {annotations.length}{' '}
                {isFr ? 'champ(s)' : 'field(s)'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Annotation modal ── */}
      <Dialog open={annModalOpen} onOpenChange={setAnnModalOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-base">
              {editingAnnId
                ? isFr
                  ? 'Modifier le champ'
                  : 'Edit field'
                : isFr
                ? 'Nouveau champ'
                : 'New field'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 mt-2">
            {/* Field key */}
            <div className="space-y-1">
              <Label className="text-xs">
                {isFr ? 'Clé du champ *' : 'Field key *'}
                <span className="text-muted-foreground font-normal ml-1">
                  (ex: stats.strength)
                </span>
              </Label>
              <Input
                value={annForm.fieldKey}
                placeholder="name, stats.strength, hp…"
                onChange={(e) =>
                  setAnnForm((p) => ({ ...p, fieldKey: e.target.value }))
                }
                className="font-mono text-sm"
              />
            </div>

            {/* Label */}
            <div className="space-y-1">
              <Label className="text-xs">
                {isFr ? 'Libellé' : 'Label'}
              </Label>
              <Input
                value={annForm.label}
                placeholder={isFr ? 'Force, Points de vie…' : 'Strength, Hit points…'}
                onChange={(e) =>
                  setAnnForm((p) => ({ ...p, label: e.target.value }))
                }
              />
            </div>

            {/* Section title */}
            <div className="space-y-1">
              <Label className="text-xs">
                {isFr ? 'Titre de section' : 'Section title'}
              </Label>
              <Input
                value={annForm.sectionTitle}
                placeholder={isFr ? 'Statistiques, Identité…' : 'Statistics, Identity…'}
                onChange={(e) =>
                  setAnnForm((p) => ({ ...p, sectionTitle: e.target.value }))
                }
              />
            </div>

            {/* Field type */}
            <div className="space-y-1">
              <Label className="text-xs">
                {isFr ? 'Type de champ' : 'Field type'}
              </Label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={annForm.fieldType}
                onChange={(e) =>
                  setAnnForm((p) => ({ ...p, fieldType: e.target.value }))
                }
              >
                {FIELD_TYPES.map((ft) => (
                  <option key={ft.value} value={ft.value}>
                    {isFr ? ft.labelFr : ft.labelEn}
                  </option>
                ))}
              </select>
            </div>

            {/* Font size */}
            <div className="space-y-1">
              <Label className="text-xs">
                {isFr ? 'Taille de police' : 'Font size'}
              </Label>
              <Input
                type="number"
                min={6}
                max={24}
                value={annForm.fontSize}
                onChange={(e) =>
                  setAnnForm((p) => ({
                    ...p,
                    fontSize: Number(e.target.value),
                  }))
                }
              />
            </div>

            {/* resource_bar: max key */}
            {annForm.fieldType === 'resource_bar' && (
              <div className="space-y-1">
                <Label className="text-xs">
                  {isFr ? 'Clé du maximum' : 'Max field key'}
                  <span className="text-muted-foreground font-normal ml-1">
                    (ex: hp_max)
                  </span>
                </Label>
                <Input
                  value={annForm.resourceMaxKey}
                  placeholder="hp_max"
                  onChange={(e) =>
                    setAnnForm((p) => ({
                      ...p,
                      resourceMaxKey: e.target.value,
                    }))
                  }
                  className="font-mono text-sm"
                />
              </div>
            )}

            {/* Visibility */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">
                  {isFr ? 'Visibilité' : 'Visibility'}
                </Label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={annForm.visibility}
                  onChange={(e) =>
                    setAnnForm((p) => ({ ...p, visibility: e.target.value }))
                  }
                >
                  <option value="public">
                    {isFr ? 'Public' : 'Public'}
                  </option>
                  <option value="gm_only">
                    {isFr ? 'MJ seulement' : 'GM only'}
                  </option>
                </select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">
                  {isFr ? 'Modifiable par' : 'Editable by'}
                </Label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={annForm.editable_by}
                  onChange={(e) =>
                    setAnnForm((p) => ({ ...p, editable_by: e.target.value }))
                  }
                >
                  <option value="player">
                    {isFr ? 'Joueur' : 'Player'}
                  </option>
                  <option value="gm">{isFr ? 'MJ' : 'GM'}</option>
                </select>
              </div>
            </div>

            {/* Validations: required + min/max for numbers */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ann-required"
                checked={annForm.required}
                onChange={(e) =>
                  setAnnForm((p) => ({ ...p, required: e.target.checked }))
                }
                className="h-4 w-4 rounded border-input"
              />
              <Label htmlFor="ann-required" className="text-xs cursor-pointer">
                {isFr ? 'Requis' : 'Required'}
              </Label>
            </div>

            {(annForm.fieldType === 'number' ||
              annForm.fieldType === 'resource_bar') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Min</Label>
                  <Input
                    type="number"
                    value={annForm.min}
                    onChange={(e) =>
                      setAnnForm((p) => ({ ...p, min: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Max</Label>
                  <Input
                    type="number"
                    value={annForm.max}
                    onChange={(e) =>
                      setAnnForm((p) => ({ ...p, max: e.target.value }))
                    }
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAnnModalOpen(false);
                setPendingRect(null);
                setEditingAnnId(null);
              }}
            >
              {t('cancel', language)}
            </Button>
            <Button type="button" onClick={handleSaveAnn}>
              {t('save', language)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
